import { RPC_ENDPOINT } from "@solomon-apps/config";
import { formatCurrency } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

interface TreasuryPositionConfig {
    venue: string;
    asset: string;
    walletAddress: string;
    mint: string;
    decimals: number;
    underlyingExposure: string;
}

export interface TreasuryPosition extends TreasuryPositionConfig {
    amountValue: BigDecimal;
    amount: string;
    balanceSource: "rpc" | "unavailable";
}

// Off-API positions: USDG at Paxos isn't yet returned by data-api.
// Resolved at runtime here via Helius RPC. When RPC data is unavailable the
// position remains visible for verification, but its amount is shown as `—`.
// Remove this entry once data-api ships the position.
const OFF_API_TREASURY_POSITIONS_CONFIG: TreasuryPositionConfig[] = [
    {
        venue: "Paxos",
        asset: "USDG",
        walletAddress: "CSJJKrBP32T71Zqto6hm7Yi8LXd7eBTWn7piFxCjQfsY",
        mint: "2u1tszSeqZ3qBWF3uNGPFc8TzMk2tdiwknnRMWGWjGWH",
        decimals: 6,
        underlyingExposure: "U.S. Treasury Bonds"
    }
];

const STALE_TIME_MS = 30 * 60 * 1000;
const GC_TIME_MS = 60 * 60 * 1000;

interface ParsedTokenAmount {
    amount?: string;
}

interface ParsedTokenAccountInfo {
    tokenAmount?: ParsedTokenAmount;
}

interface ParsedTokenAccountValue {
    account?: { data?: { parsed?: { info?: ParsedTokenAccountInfo } } };
}

async function fetchUsdValue(cfg: TreasuryPositionConfig): Promise<BigDecimal> {
    const res = await fetch(RPC_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            jsonrpc: "2.0",
            id: 1,
            method: "getTokenAccountsByOwner",
            params: [cfg.walletAddress, { mint: cfg.mint }, { encoding: "jsonParsed" }]
        })
    });
    if (!res.ok) throw new Error(`RPC HTTP ${res.status}`);

    const json: {
        error?: { message: string };
        result?: { value?: ParsedTokenAccountValue[] };
    } = await res.json();
    if (json.error) throw new Error(json.error.message);

    // Sum all matching token accounts (defensive — usually exactly one ATA).
    let totalRaw = new BigDecimal("0");
    for (const item of json.result?.value ?? []) {
        const raw = item.account?.data?.parsed?.info?.tokenAmount?.amount;
        if (raw) totalRaw = totalRaw.add(new BigDecimal(raw));
    }
    // USDG is a 1:1 stablecoin, so human token amount == USD value.
    return totalRaw.divide(new BigDecimal((10 ** cfg.decimals).toString()));
}

function resolve(
    cfg: TreasuryPositionConfig,
    amountValue: BigDecimal,
    balanceSource: TreasuryPosition["balanceSource"]
): TreasuryPosition {
    return {
        ...cfg,
        amountValue,
        amount: balanceSource === "rpc" ? formatCurrency(amountValue) : "—",
        balanceSource
    };
}

export function useOffApiTreasuryBalancesQuery(): UseQueryResult<TreasuryPosition[]> {
    return useQuery<TreasuryPosition[]>({
        queryKey: ["off-api-treasury-balances"],
        queryFn: async () => {
            const settled = await Promise.allSettled(
                OFF_API_TREASURY_POSITIONS_CONFIG.map((cfg) => fetchUsdValue(cfg))
            );
            return OFF_API_TREASURY_POSITIONS_CONFIG.map((cfg, i) => {
                const r = settled[i];
                return r.status === "fulfilled"
                    ? resolve(cfg, r.value, "rpc")
                    : resolve(cfg, new BigDecimal("0"), "unavailable");
            });
        },
        staleTime: STALE_TIME_MS,
        gcTime: GC_TIME_MS,
        refetchOnWindowFocus: false,
        // Keep the verification row visible while the live balance loads.
        placeholderData: OFF_API_TREASURY_POSITIONS_CONFIG.map((cfg) =>
            resolve(cfg, new BigDecimal("0"), "unavailable")
        )
    });
}

export function useOffApiTreasuryBalances(): TreasuryPosition[] {
    const { data } = useOffApiTreasuryBalancesQuery();
    return data ?? [];
}
